/**
 * Filter section for the ASNTab.
 *
 * Extracted to reduce ASNTab line count.
 * CR-6449: Added new filter props for Warehouse, Supplier Country,
 *          Part number, Description, and Quick select date chips.
 *          Supplier view gets collapsible accordion design with header bar.
 *          Customer view gets flat layout with footer buttons.
 * CR-6441: Added Division and Company Country cascade filter support.
 */

import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { ASNFilterSections } from './asn/ASNFilterSections';

interface FilterOption {
  value: string;
  label: string;
}

interface ASNFilterSectionProps {
  filters: {
    asnNumber: string;
    supplier: string;
    trackingNumber: string;
    poNumber: string;
    poLineNumber: string;
    partNumber: string;
    description: string;
    status: string;
    courier: string;
    courierDeliveryStatus: string;
    dateFrom: string;
    dateTo: string;
    site: string;
    warehouse: string;
    supplierCountry: string;
    supplierGroup: string;
    commodityGroup: string;
    commodityType: string;
    strategic: string;
    // CR-6441: New cascade filter fields
    division?: string;
    companyCountry?: string;
  };
  textInputs: {
    asnNumber: string;
    supplier: string;
    trackingNumber: string;
    poNumber: string;
    poLineNumber: string;
    partNumber: string;
    description: string;
  };
  siteOptions: FilterOption[];
  warehouseOptions: FilterOption[];
  supplierCountryOptions: FilterOption[];
  supplierGroupOptions: FilterOption[];
  commodityGroupOptions: FilterOption[];
  commodityTypeOptions: FilterOption[];
  quickSelectValue: string;
  onFilterChange: (key: string, value: string) => void;
  onQuickSelectChange: (value: string) => void;
  onClearFilters: () => void;
  onSearch: () => void;
  onHideFilters?: () => void;
  /** CR-6449: Whether this is supplier view - controls which fields are shown */
  isSupplier?: boolean;
  /** CR-6441: Division options for cascade filtering */
  divisionOptions?: FilterOption[];
  /** CR-6441: Site country options for cascade filtering */
  siteCountries?: string[];
  /** CR-6160: Whether ASN logistics tracking module is enabled - gates courier status filter */
  trackingEnabled?: boolean;
}

export function ASNFilterSection({
  filters,
  textInputs,
  siteOptions,
  warehouseOptions,
  supplierCountryOptions,
  supplierGroupOptions,
  commodityGroupOptions,
  commodityTypeOptions,
  quickSelectValue,
  onFilterChange,
  onQuickSelectChange,
  onClearFilters,
  onSearch,
  onHideFilters,
  isSupplier = false,
  divisionOptions = [],
  siteCountries = [],
  trackingEnabled = false,
}: ASNFilterSectionProps) {
  // CR-6449: Supplier view renders its own header bar and styling inside ASNFilterSections
  if (isSupplier) {
    return (
      <Card>
        <CardContent className="pt-4 pb-4">
          <ASNFilterSections
            filters={filters as unknown as Record<string, string>}
            textInputs={textInputs as unknown as Record<string, string>}
            siteOptions={siteOptions}
            warehouseOptions={warehouseOptions}
            supplierCountryOptions={supplierCountryOptions}
            supplierGroupOptions={supplierGroupOptions}
            commodityGroupOptions={commodityGroupOptions}
            commodityTypeOptions={commodityTypeOptions}
            quickSelectValue={quickSelectValue}
            onFilterChange={onFilterChange}
            onQuickSelectChange={onQuickSelectChange}
            onApply={onSearch}
            onClearFilters={onClearFilters}
            onHideFilters={onHideFilters}
            isSupplier={isSupplier}
            divisionOptions={divisionOptions}
            siteCountries={siteCountries}
            trackingEnabled={trackingEnabled}
          />
        </CardContent>
      </Card>
    );
  }

  // Customer View - Card with header, then filter sections with footer buttons inside
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Filter ASNs</CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <ASNFilterSections
          filters={filters as unknown as Record<string, string>}
          textInputs={textInputs as unknown as Record<string, string>}
          siteOptions={siteOptions}
          warehouseOptions={warehouseOptions}
          supplierCountryOptions={supplierCountryOptions}
          supplierGroupOptions={supplierGroupOptions}
          commodityGroupOptions={commodityGroupOptions}
          commodityTypeOptions={commodityTypeOptions}
          quickSelectValue={quickSelectValue}
          onFilterChange={onFilterChange}
          onQuickSelectChange={onQuickSelectChange}
          onApply={onSearch}
          onClearFilters={onClearFilters}
          onHideFilters={onHideFilters}
          isSupplier={isSupplier}
          divisionOptions={divisionOptions}
          siteCountries={siteCountries}
          trackingEnabled={trackingEnabled}
        />
      </CardContent>
    </Card>
  );
}
